import { AdministrationHelper } from "@/Administration/Helpers/AdministrationHelper";
import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { EnvironmentTax } from "@/Common/Helpers/EnvironmentTax";
import { SiteCreateState } from "../SiteCreate";

interface ITaxesSectionProps {
    addresses: Address[];
    genericTax: number;
    gst: number;
    qst: number;
    hst: number;
    mutateState: (mutation: (state: SiteCreateState) => void, then?: () => void) => void;
}

function TaxesSection(props: ITaxesSectionProps): JSX.Element {
    const {
        addresses,
        genericTax,
        qst,
        gst,
        hst,
        mutateState
    } = props;

    const preferredAddress = Address.getDefaultAddress(addresses);
    const isCanadaSite = Address.isAddressInCanada(preferredAddress);
    const taxRate = AdministrationHelper.getTaxesForAddress(preferredAddress);
    const currentRegionCode = Address.getRegionLookupCode(preferredAddress);

    const section = [];
    if (!taxRate && isCanadaSite)
        return null;

    function renderHst(): JSX.Element {
        return (
            <div className="row" style={{ marginBottom: "15px" }}>
                <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                    <label className="form-label">{cacheStore.GetString("HstMLabel")}%</label>
                </div>
                <div className="col-lg-5 col-md-9 col-sm-12 ">
                    <TextboxControl
                        debounceMs={200}
                        className="form-control"
                        IsNumeric={true}
                        onChange={hst => mutateState(s => s.hst = hst)}
                        value={hst?.toString()}
                        valid={!IsNullOrWhiteSpace(hst?.toString())}
                        maxLength={8}
                        maxDecimal={4}
                    />
                </div>
            </div>
        );
    }

    function renderGenericTax(): JSX.Element {
        return (
            <div className="row" style={{ marginBottom: "15px" }}>
                <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                    <label className="form-label">{cacheStore.GetString("TaxLabel")}%</label>
                </div>
                <div className="col-lg-5 col-md-9 col-sm-12 ">
                    <TextboxControl
                        debounceMs={200}
                        className="form-control"
                        IsNumeric={true}
                        onChange={genericTax => mutateState(s => s.genericTax = genericTax)}
                        value={genericTax?.toString()}
                        valid={!IsNullOrWhiteSpace(genericTax?.toString())}
                        maxLength={8}
                        maxDecimal={4}
                    />
                </div>
            </div>
        );
    }

    function renderGst(): JSX.Element {
        return (
            <div className="row" style={{ marginBottom: "15px" }}>
                <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                    <label className="form-label">{cacheStore.GetString("GstMLabel")}%</label>
                </div>
                <div className="col-lg-5 col-md-9 col-sm-12 ">
                    <TextboxControl
                        debounceMs={200}
                        className="form-control"
                        IsNumeric={true}
                        onChange={gst => mutateState(s => s.gst = gst)}
                        value={gst?.toString()}
                        valid={!IsNullOrWhiteSpace(gst?.toString())}
                        maxLength={8}
                        maxDecimal={4}
                    />
                </div>
            </div>
        );
    }

    function renderPstOrQst(label: string): JSX.Element {
        return (
            <div className="row" style={{ marginBottom: "15px" }}>
                <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                    <label className="form-label">{cacheStore.GetString(label)}%</label>
                </div>
                <div className="col-lg-5 col-md-9 col-sm-12 ">
                    <TextboxControl
                        debounceMs={200}
                        className="form-control"
                        IsNumeric={true}
                        onChange={qst => mutateState(s => s.qst = qst)}
                        value={qst?.toString()}
                        valid={!IsNullOrWhiteSpace(qst?.toString())}
                        maxLength={8}
                        maxDecimal={4}
                    />
                </div>
            </div>
        );
    }

    if (EnvironmentTax.isGstProvinceByLookupCode(currentRegionCode))
        section.push(renderGst());

    if (EnvironmentTax.isPstProvinceByLookupCode(currentRegionCode))
        section.push(renderPstOrQst("PstMLabel"));

    if (EnvironmentTax.isQstProvinceByLookupCode(currentRegionCode))
        section.push(renderPstOrQst("QstMLabel"));

    if (EnvironmentTax.isHstProvinceByLookupCode(currentRegionCode))
        section.push(renderHst());

    if (!isCanadaSite)
        section.push(renderGenericTax());

    return (
        <div>
            <div className="view-section-header">
                <h3 className="view-section-title">{cacheStore.GetString("TaxesLabel")}</h3>
            </div>
            <div className="view-section-body">
                {section}
            </div>
        </div>
    );
}

export default TaxesSection;
